import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getDadosCliente } from "@/services/mockDB";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  BarChart,
  Clock,
  Users,
  FileText,
  MapPin,
  ArrowLeft,
  ArrowRight,
  Calendar,
  Download,
  Upload,
  ShieldCheck,
  Folder,
  ChevronRight,
  Building,
  Plane,
  Car,
  Trophy,
  Hotel,
  Plus,
  Wallet,
  Sprout,
  Cloud,
  TrendingDown,
  Settings,
  HelpCircle,
  Calculator,
  Bus
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";

const ProjectDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("categories");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(["comunidades"]));
  const [selectedItem, setSelectedItem] = useState<{category: string, item: any} | null>(null);
  const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);
  const [activeParamsTab, setActiveParamsTab] = useState("transporte");
  const [activeTransportSubTab, setActiveTransportSubTab] = useState("aereo");
  const [simulacaoValor, setSimulacaoValor] = useState("1000");
  const [isSimulacaoOpen, setIsSimulacaoOpen] = useState(false);
  const [resultadoSimulacao, setResultadoSimulacao] = useState<{
    percentual: number;
    totalMSC: number;
    fundoMudas: number;
    projetos: number;
    saldoCliente: number;
    percentualMudas?: number;
    percentualSocial?: number;
    percentualCarteira?: number;
  } | null>(null);
  
  const [conversionParams, setConversionParams] = useState({
    // Parâmetros gerais
    taxaCO2ParaMSC: 50, // 50 MSC por kg de CO2
    taxaCO2ParaMudas: 0.06, // 0.06 kg CO2 por muda
    taxaConversaoReal: 50.00, // R$ 50,00 por tCO2
    tetoMaximoHospede: 100.00, // Teto máximo por hóspede em MSC
    raioCompensacaoPadrao: 150, // Raio de compensação padrão em km
    tetoMaximoTorcedor: 20.00, // Teto máximo por torcedor em MSC
    raioMedioTorcida: 30, // Raio médio de torcida em km
    // Parâmetros de Transporte - Aéreo
    transporteAereo: {
      fatorEmissaoMedio: 0.10, // kg CO2/km
      penalidadeLTO: 15.00, // kg CO2 por conexão
      fatorAjusteDistancia: 5.0, // % de segurança
      cashbackRegressivo: [
        { 
          id: 1,
          min: 200, 
          max: 600, 
          percentualTotal: 4.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 2,
          min: 601, 
          max: 800, 
          percentualTotal: 2.5, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 3,
          min: 801, 
          max: 1200, 
          percentualTotal: 2.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 4,
          min: 1201, 
          max: 2000, 
          percentualTotal: 1.5, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 5,
          min: 2001, 
          max: 99999, // "Acima de"
          percentualTotal: 1.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        }
      ]
    },
    // Parâmetros de Transporte - Carro
    transporteCarro: {
      fatorEmissao: 0.19, // kg CO2/km
      fatorAjusteDistancia: 5.0 // % de segurança
    },
    // Parâmetros de Transporte - Ônibus
    transporteOnibus: {
      fatorEmissao: 0.05, // kg CO2/km
      fatorAjusteDistancia: 5.0 // % de segurança
    }
  });
  
  const [originalParams, setOriginalParams] = useState({
    taxaCO2ParaMSC: 50,
    taxaCO2ParaMudas: 0.06,
    taxaConversaoReal: 50.00,
    tetoMaximoHospede: 100.00,
    raioCompensacaoPadrao: 150,
    tetoMaximoTorcedor: 20.00,
    raioMedioTorcida: 30,
    transporteAereo: {
      fatorEmissaoMedio: 0.10,
      penalidadeLTO: 15.00,
      fatorAjusteDistancia: 5.0,
      cashbackRegressivo: [
        { 
          id: 1,
          min: 200, 
          max: 600, 
          percentualTotal: 4.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 2,
          min: 601, 
          max: 800, 
          percentualTotal: 2.5, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 3,
          min: 801, 
          max: 1200, 
          percentualTotal: 2.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 4,
          min: 1201, 
          max: 2000, 
          percentualTotal: 1.5, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        },
        { 
          id: 5,
          min: 2001, 
          max: 99999,
          percentualTotal: 1.0, 
          split: { mudas: 17.5, social: 17.5, carteira: 65.0 }
        }
      ]
    },
    transporteCarro: {
      fatorEmissao: 0.19,
      fatorAjusteDistancia: 5.0
    },
    transporteOnibus: {
      fatorEmissao: 0.05,
      fatorAjusteDistancia: 5.0
    }
  });

  // Verificar se houve alterações nos parâmetros
  const hasChanges = () => {
    // Comparar parâmetros gerais
    if (
      conversionParams.taxaCO2ParaMSC !== originalParams.taxaCO2ParaMSC ||
      conversionParams.taxaCO2ParaMudas !== originalParams.taxaCO2ParaMudas ||
      conversionParams.taxaConversaoReal !== originalParams.taxaConversaoReal ||
      conversionParams.tetoMaximoHospede !== originalParams.tetoMaximoHospede ||
      conversionParams.raioCompensacaoPadrao !== originalParams.raioCompensacaoPadrao ||
      conversionParams.tetoMaximoTorcedor !== originalParams.tetoMaximoTorcedor ||
      conversionParams.raioMedioTorcida !== originalParams.raioMedioTorcida
    ) {
      return true;
    }
    
    // Comparar parâmetros de transporte
    const aereo = conversionParams.transporteAereo;
    const aereoOriginal = originalParams.transporteAereo;
    if (
      aereo.fatorEmissaoMedio !== aereoOriginal.fatorEmissaoMedio ||
      aereo.penalidadeLTO !== aereoOriginal.penalidadeLTO ||
      aereo.fatorAjusteDistancia !== aereoOriginal.fatorAjusteDistancia ||
      JSON.stringify(aereo.cashbackRegressivo) !== JSON.stringify(aereoOriginal.cashbackRegressivo)
    ) {
      return true;
    }
    
    const carro = conversionParams.transporteCarro;
    const carroOriginal = originalParams.transporteCarro;
    if (
      carro.fatorEmissao !== carroOriginal.fatorEmissao ||
      carro.fatorAjusteDistancia !== carroOriginal.fatorAjusteDistancia
    ) {
      return true;
    }
    
    const onibus = conversionParams.transporteOnibus;
    const onibusOriginal = originalParams.transporteOnibus;
    if (
      onibus.fatorEmissao !== onibusOriginal.fatorEmissao ||
      onibus.fatorAjusteDistancia !== onibusOriginal.fatorAjusteDistancia
    ) {
      return true;
    }
    
    return false;
  };

  // Função para abrir o modal e resetar os valores
  const handleOpenParamsModal = () => {
    // Salvar os valores atuais como originais quando abrir o modal
    setOriginalParams(JSON.parse(JSON.stringify(conversionParams)));
    setIsParamsModalOpen(true);
  };

  // Função para cancelar e reverter alterações
  const handleCancelParams = () => {
    // Reverter para os valores originais
    setConversionParams(JSON.parse(JSON.stringify(originalParams)));
    setIsParamsModalOpen(false);
  };

  // Função para salvar os parâmetros
  const handleSaveParams = () => {
    // Criar uma cópia profunda dos novos valores
    const newParams = JSON.parse(JSON.stringify(conversionParams));
    
    // Atualizar os valores originais com os novos valores salvos
    setOriginalParams(newParams);
    
    // Garantir que conversionParams também está atualizado
    setConversionParams(newParams);
    
    setIsParamsModalOpen(false);
    toast({
      title: "Parâmetros salvos com sucesso!",
      description: "Todos os parâmetros de conversão foram atualizados.",
    });
  };
  
  // Função para calcular simulação de cashback
  const calcularSimulacaoCashback = (valor: number) => {
    const faixa = conversionParams.transporteAereo.cashbackRegressivo.find(
      f => valor >= f.min && (f.max === 99999 || valor <= f.max)
    );
    if (!faixa) return { 
      percentual: 0, 
      totalMSC: 0, 
      fundoMudas: 0, 
      projetos: 0, 
      saldoCliente: 0,
      percentualMudas: 0,
      percentualSocial: 0,
      percentualCarteira: 0
    };
    
    const totalMSC = (valor * faixa.percentualTotal) / 100;
    const fundoMudas = (totalMSC * faixa.split.mudas) / 100;
    const projetos = (totalMSC * faixa.split.social) / 100;
    const saldoCliente = (totalMSC * faixa.split.carteira) / 100;
    
    return {
      percentual: faixa.percentualTotal,
      totalMSC: totalMSC,
      fundoMudas: fundoMudas,
      projetos: projetos,
      saldoCliente: saldoCliente,
      percentualMudas: faixa.split.mudas,
      percentualSocial: faixa.split.social,
      percentualCarteira: faixa.split.carteira
    };
  };

  // Função para executar o cálculo quando o botão for clicado
  const handleCalcularSimulacao = () => {
    const valor = parseFloat(simulacaoValor);
    if (valor > 0) {
      const resultado = calcularSimulacaoCashback(valor);
      setResultadoSimulacao(resultado);
    } else {
      setResultadoSimulacao(null);
    }
  };

  // Dados de empresas por categoria (em produção viria de uma API)
  const companiesByCategory: Record<string, number> = {
    "comunidades": 1, // Cacuia Colônia Z10
    "transportes": 0, // Subcategorias (Aéreo e Terrestre) têm suas próprias empresas
    "aereo": 1, // Aviões S.A.
    "terrestre": 0,
    "esportes": 0,
    "hotel": 1 // Hotel Mar do Brasil Ltda
  };

  // Função para contar itens de uma categoria
  const getCategoryItemCount = (category: any) => {
    // Se a categoria tem items definidos, usa o length
    if (category.items && category.items.length > 0) {
      return category.items.length;
    }
    // Caso contrário, busca no mapeamento de empresas
    return companiesByCategory[category.id] || 0;
  };

  // Estados para métricas dinâmicas da empresa AVIÕES
  const [totalMSCEmitido, setTotalMSCEmitido] = useState(0);
  const [totalMudas, setTotalMudas] = useState(0);
  const [totalCO2Toneladas, setTotalCO2Toneladas] = useState(0);

  // Dados agregados das empresas (em produção viria de uma API)
  // Baseado nos dados reais das empresas Aviões S.A. e Hotel Mar do Brasil
  const empresasData: Record<string, any> = {
    "aereo": {
      // Aviões S.A.
      empresas: 1,
      viagens: 2, // 2 de João
      distancia: 2740, // km
      co2Kg: 1.21, // kg
      msc: 60.5
    },
    "hotel": {
      // Hotel Mar do Brasil
      empresas: 1,
      checkIns: 3, // 3 de João
      distancia: 3820, // km
      co2Kg: 509.4, // kg
      msc: 5094
    },
    "transportes": {
      // Agregação de Aéreo - Aviões S.A. (João)
      empresas: 1,
      viagens: 2,
      distancia: 2496, // 366 + 2130
      co2Kg: 288, // 60 + 228
      msc: 115.00 // 45.00 + 70.00
    }
  };

  // Buscar dados reais da empresa AVIÕES (companyId "1")
  useEffect(() => {
    const calcularMetricasAvioes = () => {
      const CLIENT_ID = "1"; // Cliente João Silva da empresa AVIÕES
      const cliente = getDadosCliente(CLIENT_ID);
      
      if (!cliente) {
        return;
      }

      // 1. Total Cashbacks Gerados (MSC Geradas) - soma ENTRADAS de cashback de viagem
      const transacoes = cliente.transacoes || [];
      const totalCashbacks = transacoes
        .filter(t => t.tipoTransacao === 'entrada' && t.tipo === 'Entrada' && t.destino === 'Cashback de Viagem')
        .reduce((acc, curr) => acc + (Number(curr.valor) || 0), 0);
      setTotalMSCEmitido(totalCashbacks);

      // 2. Mudas Plantadas - baseado em saídas para mudas / 6
      const totalDoadoMudas = transacoes
        .filter(t => t.tipo === 'Apoio a Mudas' || (t.tipo && t.tipo.includes('Mudas')))
        .reduce((acc, curr) => acc + (Number(Math.abs(curr.valor)) || 0), 0);
      const mudas = Math.floor(totalDoadoMudas / 6);
      setTotalMudas(mudas);

      // 3. CO2 Compensado - buscar das viagens do cliente
      // Dados mockados das viagens (em produção viria de uma API)
      // Mesma estrutura usada no CompanyDetail
      const clientsData: Record<string, any> = {
        "1": {
          id: "1",
          viagens: [
            { 
              id: "1", 
              data: "20/12/2025", 
              rota: "SDU - GRU", 
              distancia: "366 km", 
              emissao: "60 kg",
              valorPassagem: 500.00,
              cashbackTotal: 20.00
            }
          ]
        }
      };

      const clientData = clientsData[CLIENT_ID];
      let totalCO2Kg = 0;
      
      if (clientData && clientData.viagens) {
        clientData.viagens.forEach((viagem: any) => {
          const emissaoStr = viagem.emissao.replace(" kg", "").replace(",", ".").trim();
          const emissaoKg = parseFloat(emissaoStr) || 0;
          totalCO2Kg += emissaoKg;
        });
      }

      // Converter CO2 de kg para toneladas
      const totalCO2Ton = totalCO2Kg / 1000;
      setTotalCO2Toneladas(totalCO2Ton);
    };

    calcularMetricasAvioes();

    // Listener para atualizações do localStorage
    const handleStorageUpdate = () => {
      calcularMetricasAvioes();
    };

    window.addEventListener('mockDB:updated', handleStorageUpdate);
    window.addEventListener('storage', handleStorageUpdate);

    return () => {
      window.removeEventListener('mockDB:updated', handleStorageUpdate);
      window.removeEventListener('storage', handleStorageUpdate);
    };
  }, []);

  // Função para obter métricas de uma categoria
  const getCategoryMetrics = (categoryId: string) => {
    if (categoryId === "transportes") {
      const data = empresasData.transportes;
      return {
        empresas: data.empresas,
        viagens: data.viagens,
        distancia: data.distancia,
        msc: data.msc
      };
    } else if (categoryId === "hotel") {
      const data = empresasData.hotel;
      return {
        empresas: data.empresas,
        checkIns: data.checkIns,
        msc: data.msc
      };
    } else if (categoryId === "aereo") {
      const data = empresasData.aereo;
      return {
        empresas: data.empresas,
        viagens: data.viagens,
        distancia: data.distancia,
        msc: data.msc
      };
    }
    return null;
  };

  // Mock data for the Moeda Social Carbono project
  const project = {
    id: "BLC-10285",
    name: "Moeda Social Carbono",
    registryId: "54321",
    status: "Ativo",
    location: "Nacional",
    region: "Brasil",
    type: "Mitigação",
    date: "01/12/2024",
    progress: 75,
    description: "MOEDA SOCIAL CARBONO\n\nO projeto propõe a realização de inventário de emissões de gases de efeito estufa de unidades residenciais, comerciais e/ou empresas, com apoio de cadastro online e/ou presencial, tudo com apoio de uma Calculadora de Emissões de Gases.",
    coordinator: "Marcos Lacerda",
    team: [],
    budget: "R$ 350.000,00",
    startDate: "01/12/2024",
    endDate: "15/09/2026",
    partners: ["Prefeitura do rio Meio Ambiente e Clima", "CEA", "Colônia de pescadores z10"],
    supportedBy: "Apoio: Moove",
    categories: [
      {
        id: "comunidades",
        name: "Comunidades",
        icon: Building,
        items: [
          {
            id: "colonia-z10",
            name: "Cacuia Colônia Z10",
            location: "Cacuia, RJ",
            status: "Ativo",
            progress: 75,
            description: "Projeto de inventário de emissões de GEE das unidades residenciais de 40 domicílios da Cacuia Colônia Z10"
          }
        ]
      },
      {
        id: "transportes",
        name: "Transportes",
        icon: Car,
        items: [
          {
            id: "aereo",
            name: "Aéreo",
            location: "Nacional",
            status: "Planejamento",
            progress: 0,
            description: "Categoria de transportes aéreos"
          },
          {
            id: "terrestre",
            name: "Terrestre",
            location: "Nacional",
            status: "Planejamento",
            progress: 0,
            description: "Categoria de transportes terrestres"
          }
        ]
      },
      {
        id: "esportes",
        name: "Esportes",
        icon: Trophy,
        items: []
      },
      {
        id: "hotel",
        name: "Hotelaria",
        icon: Hotel,
        items: [
          {
            id: "hotel-mar-brasil",
            name: "Hotel Mar do Brasil Ltda",
            location: "Nacional",
            status: "Ativo",
            progress: 0,
            description: "Empresa de hotelaria"
          }
        ]
      }
    ],
    milestones: [
      { name: "Planejamento e estruturação", date: "01/12/2024", status: "Completo" },
      { name: "Desenvolvimento da plataforma", date: "25/02/2025", status: "Em andamento" },
      { name: "Treinamento comunitário", date: "10/04/2025", status: "Pendente" },
      { name: "Lançamento da moeda", date: "20/06/2025", status: "Pendente" },
      { name: "Monitoramento inicial", date: "15/08/2025", status: "Pendente" }
    ],
    documents: [
      { name: "Projeto Executivo", type: "PDF", date: "15/11/2024", size: "3.5 MB" },
      { name: "Contratos de Parceria", type: "DOCX", date: "05/12/2024", size: "1.2 MB" },
      { name: "Análise de Viabilidade", type: "PDF", date: "20/11/2024", size: "2.8 MB" },
      { name: "Relatório Técnico Preliminar", type: "PDF", date: "01/12/2024", size: "4.1 MB" }
    ],
    transactions: [
      { id: "TX-1234", date: "05/12/2024", type: "Emissão", amount: "100", value: "R$ 1.500,00" },
      { id: "TX-1235", date: "10/12/2024", type: "Troca", amount: "25", value: "R$ 375,00" },
      { id: "TX-1236", date: "15/12/2024", type: "Compra", amount: "50", value: "R$ 750,00" }
    ],
    impactMetrics: {
      co2Reduction: "4.879,99 KgCO₂e",
      communitiesEngaged: "1",
      directBeneficiaries: "40",
      jobs: "?"
    }
  };

  const getMilestoneStatusColor = (status: string) => {
    switch (status) {
      case "Completo":
        return "bg-green-100 text-green-800";
      case "Em andamento":
        return "bg-blue-100 text-blue-800";
      case "Pendente":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="container mx-auto p-4 space-y-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/projects">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <img 
            src="/images/Logo Terrazul.png" 
            alt="Logo Terrazul"
            className="h-16 w-auto object-contain"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">{project.name}</h1>
            <p className="text-muted-foreground">ID: {project.id}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800`}>
              {project.status}
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Localização</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span>{project.location}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Período</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span>
              {project.startDate} - {project.status === "Ativo" ? "Hoje" : project.endDate}
            </span>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard de Economia da Moeda */}
      <div className="space-y-4">
        <h2 className="text-xl font-bold text-gray-800">Economia da Moeda</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Card 1: MSC Geradas */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Wallet className="h-4 w-4 text-blue-600" />
                    <div className="text-sm text-muted-foreground">💰 MSC Geradas</div>
                  </div>
                  <div className="text-3xl font-bold text-blue-600">
                    MSC {totalMSCEmitido.toFixed(2).replace(".", ",")}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 2: Mudas Plantadas */}
          <Card className="bg-green-50 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Sprout className="h-4 w-4 text-green-600" />
                    <div className="text-sm text-muted-foreground">🌳 Mudas Plantadas</div>
                  </div>
                  <div className="text-3xl font-bold text-green-600">
                    {totalMudas} {totalMudas === 1 ? 'Árvore' : 'Árvores'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card 3: CO2 Compensado */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Cloud className="h-4 w-4 text-[#0e7cb4]" />
                    <div className="text-sm text-muted-foreground">☁️ CO₂ Compensado</div>
                  </div>
                  <div className="text-3xl font-bold">
                    {totalCO2Toneladas.toFixed(2).replace(".", ",")} t
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <Tabs 
        defaultValue="categories" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        <TabsList className="grid w-full md:w-auto md:inline-flex">
          <TabsTrigger value="categories">Categorias</TabsTrigger>
        </TabsList>
        
        <TabsContent value="categories" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Categorias do Projeto</CardTitle>
                  <CardDescription>Organize e gerencie as categorias do projeto Moeda Social Carbono</CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleOpenParamsModal}
                  className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a] border-[#0e7cb4]"
                >
                  <Settings className="mr-2 h-4 w-4" />
                  Parâmetros de Conversão
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {project.categories.map((category) => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.has(category.id);
                
                return (
                  <div key={category.id} className="border rounded-lg">
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={(e) => {
                        // Se clicar diretamente na categoria Hotelaria ou Esportes (sem itens), navegar para página de empresas
                        if ((category.id === "hotel" || category.id === "esportes") && getCategoryItemCount(category) === 0) {
                          navigate(`/admin/projects/${category.id}`);
                          return;
                        }
                        const newExpanded = new Set(expandedCategories);
                        if (isExpanded) {
                          newExpanded.delete(category.id);
                        } else {
                          newExpanded.add(category.id);
                        }
                        setExpandedCategories(newExpanded);
                      }}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Icon className="h-5 w-5 text-[#0e7cb4]" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{category.name}</h3>
                          <div className="flex items-center gap-4 mt-1 flex-wrap">
                            <p className="text-sm text-muted-foreground">
                              {getCategoryItemCount(category)} {getCategoryItemCount(category) === 1 ? 'item' : 'itens'}
                            </p>
                            {(() => {
                              const metrics = getCategoryMetrics(category.id);
                              if (!metrics) return null;
                              
                              if (category.id === "transportes") {
                                return (
                                  <>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm text-muted-foreground">{metrics.viagens || 0} {metrics.viagens === 1 ? 'Viagem' : 'Viagens'}</p>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm text-muted-foreground">{metrics.empresas} {metrics.empresas === 1 ? 'Empresa Ativa' : 'Empresas Ativas'}</p>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm text-muted-foreground">{metrics.distancia.toLocaleString('pt-BR')} km rodados</p>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm font-semibold text-green-600">🟢 MSC {metrics.msc.toFixed(2).replace(".", ",")} gerados</p>
                                  </>
                                );
                              } else if (category.id === "hotel") {
                                return (
                                  <>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm text-muted-foreground">-- {metrics.empresas === 1 ? 'Hotel' : 'Hoteis'}</p>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm text-muted-foreground">-- Check-ins</p>
                                    <span className="text-sm text-muted-foreground">|</span>
                                    <p className="text-sm font-semibold text-green-600">🟢 MSC --,-- gerados</p>
                                  </>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        </div>
                      </div>
                      <ChevronRight 
                        className={`h-5 w-5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      />
                    </div>
                    
                    {isExpanded && category.items.length > 0 && (
                      <div className="border-t p-4 space-y-3">
                        {category.items.map((item) => (
                          <Card key={item.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="font-semibold mb-1">{item.name}</h4>
                                  <p className="text-sm text-muted-foreground mb-2">{item.description}</p>
                                  <div className="flex items-center gap-4 text-sm">
                                    <div className="flex items-center gap-1">
                                      <MapPin className="h-4 w-4 text-muted-foreground" />
                                      <span>{item.location}</span>
                                    </div>
                                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                      item.status === "Ativo" ? "bg-green-100 text-green-800" :
                                      item.status === "Em andamento" ? "bg-blue-100 text-blue-800" :
                                      "bg-yellow-100 text-yellow-800"
                                    }`}>
                                      {item.status}
                                    </span>
                                  </div>
                                </div>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => {
                                    // Se for um item de subcategoria (Aéreo ou Terrestre), navegar para página de empresas
                                    if (category.id === "transportes" && (item.id === "aereo" || item.id === "terrestre")) {
                                      navigate(`/admin/projects/${category.id}/${item.id}`);
                                    } else if (category.id === "hotel" && item.id === "hotel-mar-brasil") {
                                      // Para Hotel Mar do Brasil, navegar para página de detalhes da empresa
                                      navigate(`/admin/companies/hotel-mar-brasil`);
                                    } else {
                                      // Para outros itens (como Colônia Z10), navegar para página completa do projeto
                                      navigate(`/projeto/10`);
                                    }
                                  }}
                                >
                                  Ver mais
                                  <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                    
                    {isExpanded && category.items.length === 0 && (
                      <div className="border-t p-4 text-center text-muted-foreground">
                        <p>Nenhum item nesta categoria ainda.</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="mt-2"
                          onClick={() => {
                            // Se for Hotelaria ou Esportes, navegar para página de empresas
                            if (category.id === "hotel" || category.id === "esportes") {
                              navigate(`/admin/projects/${category.id}`);
                            }
                          }}
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Adicionar item
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Detalhes do Item */}
      <Dialog open={selectedItem !== null} onOpenChange={(open) => !open && setSelectedItem(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedItem && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedItem.item.name}</DialogTitle>
                <DialogDescription>
                  {selectedItem.category} - {selectedItem.item.location}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="font-semibold mb-2">Descrição</h3>
                  <p className="text-muted-foreground">{selectedItem.item.description}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold mb-2">Localização</h3>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span>{selectedItem.item.location}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2">Status</h3>
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      selectedItem.item.status === "Ativo" ? "bg-green-100 text-green-800" :
                      selectedItem.item.status === "Em andamento" ? "bg-blue-100 text-blue-800" :
                      "bg-yellow-100 text-yellow-800"
                    }`}>
                      {selectedItem.item.status}
                    </span>
                  </div>
                </div>

                {selectedItem.item.progress > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Progresso</h3>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Concluído</span>
                      <span>{selectedItem.item.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div 
                        className="bg-[#0e7cb4] h-3 rounded-full transition-all" 
                        style={{ width: `${selectedItem.item.progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" onClick={() => setSelectedItem(null)}>
                    Fechar
                  </Button>
                  <Button>
                    <Download className="mr-2 h-4 w-4" />
                    Exportar Relatório
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de Parâmetros de Conversão */}
      <Dialog open={isParamsModalOpen} onOpenChange={(open) => {
        if (!open) {
          handleCancelParams();
        } else {
          setIsParamsModalOpen(true);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>⚙️ Parâmetros de Conversão</DialogTitle>
            <DialogDescription>
              Configure os fatores de emissão e valores de conversão da Moeda Social Carbono
            </DialogDescription>
          </DialogHeader>
          
          <Tabs 
            defaultValue="transporte" 
            value={activeParamsTab} 
            onValueChange={setActiveParamsTab}
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="transporte">Transporte</TabsTrigger>
              <TabsTrigger value="hotelaria">Regras de Hotelaria</TabsTrigger>
              <TabsTrigger value="esportes">Esportes & Eventos</TabsTrigger>
            </TabsList>

            {/* Aba 1: Transporte */}
            <TabsContent value="transporte" className="space-y-4 py-4">
              {/* Parâmetros Gerais */}
              <div className="space-y-4 mb-6">
                <h3 className="font-semibold text-lg">Parâmetros Gerais</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="taxaMSC">Taxa de Conversão CO₂ → MSC</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="taxaMSC"
                        type="number"
                        step="0.1"
                        value={conversionParams.taxaCO2ParaMSC}
                        onChange={(e) => setConversionParams({
                          ...conversionParams,
                          taxaCO2ParaMSC: parseFloat(e.target.value) || 0
                        })}
                      />
                      <span className="text-sm text-muted-foreground">MSC/kg CO₂</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxaMudas">Taxa de Conversão CO₂ → Mudas</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="taxaMudas"
                        type="number"
                        step="0.01"
                        value={conversionParams.taxaCO2ParaMudas}
                        onChange={(e) => setConversionParams({
                          ...conversionParams,
                          taxaCO2ParaMudas: parseFloat(e.target.value) || 0
                        })}
                      />
                      <span className="text-sm text-muted-foreground">kg CO₂/muda</span>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="taxaReal">Taxa de Conversão Real (tCO₂ → R$)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        id="taxaReal"
                        type="number"
                        step="0.01"
                        value={conversionParams.taxaConversaoReal}
                        onChange={(e) => setConversionParams({
                          ...conversionParams,
                          taxaConversaoReal: parseFloat(e.target.value) || 0
                        })}
                      />
                      <span className="text-sm text-muted-foreground">R$/tCO₂</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sub-abas de Transporte */}
              <Tabs 
                defaultValue="aereo" 
                value={activeTransportSubTab} 
                onValueChange={setActiveTransportSubTab}
                className="w-full"
              >
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="aereo" className="flex items-center gap-2">
                    <Plane className="h-4 w-4" />
                    Aéreo (Aviação)
                  </TabsTrigger>
                  <TabsTrigger value="carro" className="flex items-center gap-2">
                    <Car className="h-4 w-4" />
                    Rodoviário (Carro)
                  </TabsTrigger>
                  <TabsTrigger value="onibus" className="flex items-center gap-2">
                    <Bus className="h-4 w-4" />
                    Rodoviário (Ônibus)
                  </TabsTrigger>
                </TabsList>

                {/* Sub-aba: Aéreo */}
                <TabsContent value="aereo" className="space-y-4 py-4">
                  {/* Painel A: Parâmetros Físicos de Voo */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">A. Parâmetros Físicos de Voo</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fatorEmissaoAereo">Fator de Emissão Médio</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorEmissaoAereo"
                              type="number"
                              step="0.01"
                              value={conversionParams.transporteAereo.fatorEmissaoMedio}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteAereo: {
                                  ...conversionParams.transporteAereo,
                                  fatorEmissaoMedio: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">kg CO₂/km</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="penalidadeLTO">Penalidade por Conexão (LTO)</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Valor fixo de CO₂ adicionado para cada escala/conexão realizada, representando o gasto extra de combustível na decolagem.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              id="penalidadeLTO"
                              type="number"
                              step="0.01"
                              value={conversionParams.transporteAereo.penalidadeLTO}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteAereo: {
                                  ...conversionParams.transporteAereo,
                                  penalidadeLTO: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">kg CO₂/conexão</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="fatorAjusteAereo">Fator de Ajuste de Distância</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Percentual de segurança adicionado à distância de mapa para compensar desvios de rota e espera.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorAjusteAereo"
                              type="number"
                              step="0.1"
                              value={conversionParams.transporteAereo.fatorAjusteDistancia}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteAereo: {
                                  ...conversionParams.transporteAereo,
                                  fatorAjusteDistancia: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Painel B: Parâmetros Financeiros (Regressividade) */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">B. Parâmetros Financeiros (Regressividade)</CardTitle>
                        <Popover open={isSimulacaoOpen} onOpenChange={(open) => {
                          setIsSimulacaoOpen(open);
                          if (!open) {
                            setResultadoSimulacao(null); // Limpar resultado ao fechar
                          }
                        }}>
                          <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="gap-2">
                              <Calculator className="h-4 w-4" />
                              Testar Parâmetros
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-80">
                            <div className="space-y-4">
                              <h4 className="font-semibold">Simulação de Cashback</h4>
                              <div className="space-y-2">
                                <Label htmlFor="simulacaoValor">Valor da Passagem (R$)</Label>
                                <div className="flex gap-2">
                                  <Input
                                    id="simulacaoValor"
                                    type="number"
                                    step="0.01"
                                    value={simulacaoValor}
                                    onChange={(e) => {
                                      setSimulacaoValor(e.target.value);
                                      setResultadoSimulacao(null); // Limpar resultado ao alterar valor
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleCalcularSimulacao();
                                      }
                                    }}
                                    placeholder="1000"
                                    className="flex-1"
                                  />
                                  <Button 
                                    onClick={handleCalcularSimulacao}
                                    className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
                                  >
                                    Calcular
                                  </Button>
                                </div>
                              </div>
                              {resultadoSimulacao && (
                                <div className="space-y-2 pt-2 border-t">
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Taxa de Cashback Aplicada:</span>
                                    <span className="font-medium">{resultadoSimulacao.percentual}%</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Total Cashback:</span>
                                    <span className="font-medium">R$ {resultadoSimulacao.totalMSC.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Fundo Mudas ({resultadoSimulacao.percentualMudas?.toFixed(1) || '0'}%):</span>
                                    <span className="font-medium">R$ {resultadoSimulacao.fundoMudas.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">Projetos ({resultadoSimulacao.percentualSocial?.toFixed(1) || '0'}%):</span>
                                    <span className="font-medium">R$ {resultadoSimulacao.projetos.toFixed(2)}</span>
                                  </div>
                                  <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                                    <span>Saldo Cliente ({resultadoSimulacao.percentualCarteira?.toFixed(1) || '0'}%):</span>
                                    <span className="text-[#0e7cb4]">R$ {resultadoSimulacao.saldoCliente.toFixed(2)}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>De R$</TableHead>
                              <TableHead>Até R$</TableHead>
                              <TableHead>% Total Cashback</TableHead>
                              <TableHead>% Fundo Mudas</TableHead>
                              <TableHead>% Projetos Sociais</TableHead>
                              <TableHead>% Saldo Cliente</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {conversionParams.transporteAereo.cashbackRegressivo.map((faixa, index) => {
                              return (
                                <TableRow key={faixa.id || index}>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="1"
                                      value={faixa.min}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        novasFaixas[index].min = parseFloat(e.target.value) || 0;
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      className="w-24"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="1"
                                      value={faixa.max === 99999 ? "" : faixa.max}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        novasFaixas[index].max = e.target.value === "" ? 99999 : (parseFloat(e.target.value) || 0);
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      placeholder={faixa.max === 99999 ? "Acima de" : ""}
                                      className="w-24"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={faixa.percentualTotal}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        novasFaixas[index].percentualTotal = parseFloat(e.target.value) || 0;
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      className="w-24"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={faixa.split.mudas}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        const novoValor = parseFloat(e.target.value) || 0;
                                        novasFaixas[index].split.mudas = novoValor;
                                        // Ajusta automaticamente o saldo do cliente para manter 100%
                                        const totalSplit = novoValor + novasFaixas[index].split.social;
                                        novasFaixas[index].split.carteira = Math.max(0, 100 - totalSplit);
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      className="w-24"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={faixa.split.social}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        const novoValor = parseFloat(e.target.value) || 0;
                                        novasFaixas[index].split.social = novoValor;
                                        // Ajusta automaticamente o saldo do cliente para manter 100%
                                        const totalSplit = novoValor + novasFaixas[index].split.mudas;
                                        novasFaixas[index].split.carteira = Math.max(0, 100 - totalSplit);
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      className="w-24"
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Input
                                      type="number"
                                      step="0.1"
                                      value={faixa.split.carteira}
                                      onChange={(e) => {
                                        const novasFaixas = [...conversionParams.transporteAereo.cashbackRegressivo];
                                        const novoValor = parseFloat(e.target.value) || 0;
                                        novasFaixas[index].split.carteira = novoValor;
                                        setConversionParams({
                                          ...conversionParams,
                                          transporteAereo: {
                                            ...conversionParams.transporteAereo,
                                            cashbackRegressivo: novasFaixas
                                          }
                                        });
                                      }}
                                      className="w-24"
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Sub-aba: Carro */}
                <TabsContent value="carro" className="space-y-4 py-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Parâmetros de Transporte Rodoviário - Carro</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fatorEmissaoCarro">Fator de Emissão</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorEmissaoCarro"
                              type="number"
                              step="0.01"
                              value={conversionParams.transporteCarro.fatorEmissao}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteCarro: {
                                  ...conversionParams.transporteCarro,
                                  fatorEmissao: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">kg CO₂/km</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="fatorAjusteCarro">Fator de Ajuste de Distância</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Percentual de segurança adicionado à distância de mapa para compensar desvios de rota e espera.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorAjusteCarro"
                              type="number"
                              step="0.1"
                              value={conversionParams.transporteCarro.fatorAjusteDistancia}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteCarro: {
                                  ...conversionParams.transporteCarro,
                                  fatorAjusteDistancia: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Sub-aba: Ônibus */}
                <TabsContent value="onibus" className="space-y-4 py-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Parâmetros de Transporte Rodoviário - Ônibus</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="fatorEmissaoOnibus">Fator de Emissão</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorEmissaoOnibus"
                              type="number"
                              step="0.01"
                              value={conversionParams.transporteOnibus.fatorEmissao}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteOnibus: {
                                  ...conversionParams.transporteOnibus,
                                  fatorEmissao: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">kg CO₂/km</span>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label htmlFor="fatorAjusteOnibus">Fator de Ajuste de Distância</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Percentual de segurança adicionado à distância de mapa para compensar desvios de rota e espera.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              id="fatorAjusteOnibus"
                              type="number"
                              step="0.1"
                              value={conversionParams.transporteOnibus.fatorAjusteDistancia}
                              onChange={(e) => setConversionParams({
                                ...conversionParams,
                                transporteOnibus: {
                                  ...conversionParams.transporteOnibus,
                                  fatorAjusteDistancia: parseFloat(e.target.value) || 0
                                }
                              })}
                            />
                            <span className="text-sm text-muted-foreground">%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* Aba 2: Regras de Hotelaria */}
            <TabsContent value="hotelaria" className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground mb-4">
                Defina aqui as travas financeiras para a compensação automática de check-ins.
              </p>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="tetoMaximo">Teto Máximo por Hóspede (MSC)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Valor máximo em Moedas que o hotel pagará por check-in, independente da distância.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="tetoMaximo"
                    type="number"
                    step="0.01"
                    placeholder="100,00"
                    value={conversionParams.tetoMaximoHospede}
                    onChange={(e) => setConversionParams({
                      ...conversionParams,
                      tetoMaximoHospede: parseFloat(e.target.value) || 0
                    })}
                  />
                  <span className="text-sm text-muted-foreground">MSC</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="raioCompensacao">Raio de Compensação Padrão (km)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Distância média considerada caso o hóspede não informe o trajeto exato.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="raioCompensacao"
                    type="number"
                    step="1"
                    placeholder="150"
                    value={conversionParams.raioCompensacaoPadrao}
                    onChange={(e) => setConversionParams({
                      ...conversionParams,
                      raioCompensacaoPadrao: parseFloat(e.target.value) || 0
                    })}
                  />
                  <span className="text-sm text-muted-foreground">km</span>
                </div>
              </div>
            </TabsContent>

            {/* Aba 3: Esportes & Eventos */}
            <TabsContent value="esportes" className="space-y-4 py-4">
              <div>
                <h4 className="font-semibold text-sm mb-4">🏟️ Regras de Eventos Esportivos</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Defina aqui as travas financeiras para a compensação automática de deslocamentos de torcedores.
                </p>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="tetoMaximoTorcedor">Teto Máximo por Torcedor (MSC)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Valor máximo que o clube pagará para compensar o deslocamento de cada torcedor no dia do jogo.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="tetoMaximoTorcedor"
                    type="number"
                    step="0.01"
                    placeholder="20,00"
                    value={conversionParams.tetoMaximoTorcedor}
                    onChange={(e) => setConversionParams({
                      ...conversionParams,
                      tetoMaximoTorcedor: parseFloat(e.target.value) || 0
                    })}
                  />
                  <span className="text-sm text-muted-foreground">MSC</span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="raioMedioTorcida">Raio Médio de Torcida (km)</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Distância média considerada para torcedores locais.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id="raioMedioTorcida"
                    type="number"
                    step="1"
                    placeholder="30"
                    value={conversionParams.raioMedioTorcida}
                    onChange={(e) => setConversionParams({
                      ...conversionParams,
                      raioMedioTorcida: parseFloat(e.target.value) || 0
                    })}
                  />
                  <span className="text-sm text-muted-foreground">km</span>
                </div>
              </div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelParams}>
              Cancelar
            </Button>
            <Button 
              className="bg-[#0e7cb4] text-white hover:bg-[#0a6a9a]"
              onClick={handleSaveParams}
              disabled={!hasChanges()}
            >
              Salvar Parâmetros
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetail;
